import axios from "axios";
import { CONFIG } from "../config";
import { setupAuthInterceptors } from "../utils/auth";

/**
 * @typedef {'CONNECTED'|'STANDBY'|'CHARGING'|'MOVE'|'OPERATION'|'EMERGENCY'|'ERROR'|'OFFLINE'|'DISCONNECTED'} RobotStatus
 */

/**
 * @typedef {Object} LocationDto
 * @property {string} x
 * @property {string} y
 * @property {string} theta
 */

/**
 * @typedef {Object} RobotStatusData
 * @property {boolean} isOnline
 * @property {RobotStatus} status
 * @property {string|null} statusText
 * @property {string|null} errorMessage
 * @property {LocationDto|null} location
 * @property {boolean} isUndockingError
 * @property {number|null} battery
 * @property {string} connectionStatus
 * @property {number|null} liftStatus - KUKA 전용. 0=DOWN, 1=UP. OMLON은 null
 */

/**
 * @typedef {'COMMAND_MOVE'|'COMMAND_STOP'|'COMMAND_UNLOCK'} RobotCommand
 */

/**
 * @typedef {Object} SendCommandRequest
 * @property {RobotCommand} command
 * @property {string} [poiId] - COMMAND_MOVE 시 필수
 * @property {string} [name]
 */

/**
 * @typedef {'PENDING'|'EXECUTING'|'SUCCESS'|'FAILED'|'CANCELLED'} TaskStatus
 */

/**
 * @typedef {Object} SendCommandResponse
 * @property {number|null} historyId
 * @property {TaskStatus} status
 */

// location.host 기반으로 동적 API 인스턴스 생성
// CONFIG.BACKEND_URL이 이미 환경에 맞게 설정되어 있음
const getApiInstance = () => {
  const instance = axios.create({
    baseURL: CONFIG.BACKEND_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });
  setupAuthInterceptors(instance);
  return instance;
};

const api = getApiInstance();

/**
 * Get current user from localStorage
 * @returns {{ id?: string, name?: string, auth?: number }} User object
 */
const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

/**
 * Get robot status
 * @param {string} deviceId - Device ID
 * @returns {Promise<Object>} Robot status data (interceptor에서 언래핑된 data 필드)
 */
export const getRobotStatus = async (deviceId, signal) => {
  const response = await api.get(`/status/${deviceId}`, { signal });
  return response.data;
};

/**
 * Send command to robot
 * @param {string} deviceId - Device ID
 * @param {Object} command - Command object
 * @returns {Promise<Object>} Command response
 */
export const sendCommand = async (deviceId, command) => {
  const response = await api.post(`/command/${deviceId}`, command);
  return response.data;
};

/**
 * Send move command
 * @param {string} deviceId - Device ID
 * @param {string} poiId - POI ID to move to
 * @returns {Promise<Object>} Command response
 */
export const sendMoveCommand = async (deviceId, poiId, name, commandType = "COMMAND_MOVE") => {
  const user = getCurrentUser();
  const command = {
    command: commandType,
    name,
    deviceId: deviceId,
    poiId: poiId,
    userId: user.id,
    msgUuid: generateUUID(),
  };
  return sendCommand(deviceId, command);
};

/**
 * Send stop command
 * @param {string} deviceId - Device ID
 * @returns {Promise<Object>} Command response
 */
export const sendStopCommand = async (deviceId) => {
  const user = getCurrentUser();
  const command = {
    command: "COMMAND_STOP",
    deviceId: deviceId,
    userId: user.id,
    msgUuid: generateUUID(),
  };
  return sendCommand(deviceId, command);
};

/**
 * Send dock command
 * @param {string} deviceId - Device ID
 * @returns {Promise<Object>} Command response
 */
export const sendDockCommand = async (deviceId) => {
  const user = getCurrentUser();
  const command = {
    command: "COMMAND_DOCK",
    deviceId: deviceId,
    userId: user.id,
    msgUuid: generateUUID(),
  };
  return sendCommand(deviceId, command);
};

/**
 * Send unlock command
 * @param {string} deviceId - Device ID
 * @returns {Promise<Object>} Command response
 */
export const sendUnlockCommand = async (deviceId) => {
  const user = getCurrentUser();
  const command = {
    command: "COMMAND_UNLOCK",
    deviceId: deviceId,
    userId: user.id,
    msgUuid: generateUUID(),
  };
  return sendCommand(deviceId, command);
};

/**
 * Get whether device is currently busy executing a command
 * @param {string} deviceId
 * @param {AbortSignal} [signal]
 * @returns {Promise<boolean>}
 */
export const getBusyStatus = async (deviceId, signal) => {
  const response = await api.get(`/busy/${deviceId}`, { signal });
  return response.data;
};

/**
 * Get last registered command for device
 * @param {string} deviceId
 * @param {AbortSignal} [signal]
 * @returns {Promise<{command: string, poiId: string|null}>}
 */
export const getLastCommand = async (deviceId, signal) => {
  const response = await api.get(`/last-command/${deviceId}`, { signal });
  return response.data;
};

/**
 * Check server health
 * @returns {Promise<Object>} Health status
 */
export const checkHealth = async () => {
  const response = await api.get("/health");
  return response.data;
};

/**
 * Generate UUID for message tracking
 * @returns {string} UUID
 */
function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const robotApi = {
  getRobotStatus,
  sendCommand,
  sendMoveCommand,
  sendStopCommand,
  checkHealth,
};

export default robotApi;
